import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../i18n/LanguageContext";
import "./about.css";

function AboutUs() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isNe = lang === "ne";

  const heroStats = isNe
    ? [
        { value: "100%", label: "KYC-आधारित पहिचान प्रवाह" },
        { value: "24/7", label: "AI+मानव सहयोग प्रणाली" },
        { value: "द्विभाषिक", label: "अंग्रेजी + नेपाली अनुभव" },
        { value: "0", label: "लुकेका ब्रोकर शुल्क" },
      ]
    : [
        { value: "100%", label: "KYC-first identity flow" },
        { value: "24/7", label: "AI + human support escalation" },
        { value: "Dual", label: "English + Nepali experience" },
        { value: "0", label: "Hidden broker fees" },
      ];

  const pillars = isNe
    ? [
        {
          title: "विश्वास-प्रथम वास्तुकला",
          body:
            "हरेक महत्वपूर्ण लेनदेन विश्वास र सत्यापनको आधारमा डिजाइन गरिएको छ। हाम्रो लक्ष्य केवल लिस्टिङ देखाउने होइन, सुरक्षित र प्रमाणित भाडा निर्णय सम्भव बनाउने हो।",
        },
        {
          title: "प्रमाणित प्रयोगकर्ता र सम्पत्ति",
          body:
            "KYC पहिचान, दस्तावेज सत्यापन, र सम्पत्ति समीक्षा प्रक्रिया जोडेर हामी नक्कली प्रोफाइल, भ्रामक फोटो र धोखाधडी जोखिम कम गर्छौं।",
        },
        {
          title: "नेपाल-केन्द्रित उत्पादन सोच",
          body:
            "स्थानीय बजार, स्थानीय सहयोग र नेपाली-मैत्री प्रयोगकर्ता अनुभवसँग बनाइएको प्लेटफर्मले भाडावाल र घरधनी दुवैको वास्तविक समस्या समाधान गर्छ।",
        },
        {
          title: "स्पष्टता र उत्तरदायित्व",
          body:
            "प्रयोगकर्ता गतिविधि, सुरक्षा रिपोर्ट र प्लेटफर्म नीति पारदर्शी रहोस् भनेर प्रक्रियामा संयमित समीक्षा, अभिलेख सोच र अनुपालनलाई प्राथमिकता दिइएको छ।",
        },
      ]
    : [
        {
          title: "Trust-first architecture",
          body:
            "Every important workflow is designed around trust and verification. Our goal is not just listing discovery, but helping people make secure rental decisions with confidence.",
        },
        {
          title: "Verified people and properties",
          body:
            "By combining KYC identity checks, document validation, and property review workflows, we reduce fake profiles, misleading listings, and fraud risk.",
        },
        {
          title: "Nepal-native product thinking",
          body:
            "Built for the realities of Nepal's rental market, GharBhada delivers local support, practical flows, and language accessibility for both renters and owners.",
        },
        {
          title: "Clarity and accountability",
          body:
            "From moderation actions to policy and support escalation, our operating model emphasizes transparency, measurable controls, and compliance-ready behavior.",
        },
      ];

  const verificationFlow = isNe
    ? [
        {
          step: "चरण ०१",
          title: "पहिचान तह",
          body: "प्रयोगकर्ता दर्ता पछि KYC मार्गबाट आधारभूत पहिचान र खाता वैधता सुनिश्चित गरिन्छ।",
        },
        {
          step: "चरण ०२",
          title: "दस्तावेज मूल्याङ्कन",
          body: "अपलोड गरिएका कागजातको संरचना, स्पष्टता, र सान्दर्भिकता जाँचेर अपूर्ण वा जोखिमयुक्त इनपुट छुट्याइन्छ।",
        },
        {
          step: "चरण ०३",
          title: "सम्पत्ति सत्यापन",
          body: "लिस्टिङ सार्वजनिक हुनुअघि फोटो, स्थान र मुख्य विवरण समीक्षा गरी न्यूनतम गुणस्तर मापदण्ड लागू हुन्छ।",
        },
        {
          step: "चरण ०४",
          title: "जोखिम संकेत",
          body: "संदिग्ध गतिविधि, असामान्य व्यवहार वा रिपोर्टहरूमा आन्तरिक सुरक्षा फ्लोले प्राथमिकता जाँच सक्रिय गर्छ।",
        },
        {
          step: "चरण ०५",
          title: "मानव समीक्षा",
          body: "गम्भीर केसमा मानव समीक्षा, टिकट उन्नयन र प्रशासकीय हस्तक्षेपद्वारा निर्णय प्रक्रियामा थप नियन्त्रण राखिन्छ।",
        },
        {
          step: "चरण ०६",
          title: "सहयोग र सुधार",
          body: "सहयोग टिकट, सुरक्षा रिपोर्ट र प्रयोगकर्ता प्रतिक्रियामार्फत प्लेटफर्म लगातार सुधार गरिन्छ।",
        },
      ]
    : [
        {
          step: "Step 01",
          title: "Identity layer",
          body: "After signup, KYC-backed checks help validate account legitimacy and create a stronger trust baseline.",
        },
        {
          step: "Step 02",
          title: "Document assessment",
          body: "Uploaded documents are evaluated for completeness, quality, and relevance to reduce weak or risky submissions.",
        },
        {
          step: "Step 03",
          title: "Property verification",
          body: "Before a listing is discoverable, photos, location, and core information are reviewed against quality standards.",
        },
        {
          step: "Step 04",
          title: "Risk signaling",
          body: "Suspicious behavior patterns and user reports can trigger higher-priority internal safety checks.",
        },
        {
          step: "Step 05",
          title: "Human moderation",
          body: "Sensitive cases can be escalated to human reviewers and admin controls for stronger decision quality.",
        },
        {
          step: "Step 06",
          title: "Support-led improvement",
          body: "Support tickets, safety reports, and user feedback drive continuous platform hardening and UX refinement.",
        },
      ];

  const standoutFeatures = isNe
    ? [
        {
          title: "घरधनीसँग प्रत्यक्ष सन्देश",
          body: "मध्यस्थ बिना घरधनीसँग प्रत्यक्ष संवाद, द्रुत निर्णय र कम भ्रम।",
        },
        {
          title: "संरचित भाडा आवेदन",
          body: "मानकीकृत आवेदन प्रवाहले भाडावाल जानकारी व्यवस्थित र तुलना गर्न सजिलो बनाउँछ।",
        },
        {
          title: "भूमिका-आधारित ड्यासबोर्ड",
          body: "भाडावाल र घरधनीका लागि फरक ड्यासबोर्ड अनुभव, सन्दर्भअनुसार उपकरण र निर्णय संकेत।",
        },
        {
          title: "सम्पत्ति र KYC सत्यापन प्रवाह",
          body: "पहिचान र लिस्टिङ सत्यापन दुवैलाई प्रक्रियागत बनाइएको हुँदा प्लेटफर्म विश्वसनीयता बढ्छ।",
        },
        {
          title: "उन्नयनसहित AI-प्रथम सहयोग",
          body: "तुरुन्त सहायता, अनि आवश्यक पर्दा मानव एजेन्टमा उन्नयन हुने हाइब्रिड सहायता संरचना।",
        },
        {
          title: "द्विभाषिक प्लेटफर्म अनुभव",
          body: "नेपाली र अंग्रेजी दुवैमा सहज अनुभवले व्यापक प्रयोगकर्ता समूहलाई समेट्छ।",
        },
        {
          title: "सुरक्षा रिपोर्टिङ सुविधा",
          body: "संदिग्ध लिस्टिङ वा प्रयोगकर्ता रिपोर्ट गर्ने स्पष्ट मार्ग, सुरक्षा प्रतिक्रिया समय सुधार।",
        },
        {
          title: "ब्रोकरविना पारदर्शी खोज",
          body: "भाडा खोजाइलाई सरल, लागत स्पष्ट र सूचना-समृद्ध बनाउने प्लेटफर्म दर्शन।",
        },
      ]
    : [
        {
          title: "Direct owner messaging",
          body: "Faster conversations and fewer surprises through direct renter-owner communication without middlemen.",
        },
        {
          title: "Structured rental applications",
          body: "Standardized application flows make renter information clearer and easier for owners to evaluate.",
        },
        {
          title: "Role-based dashboards",
          body: "Dedicated owner and renter workspaces reduce clutter and keep each workflow focused.",
        },
        {
          title: "Property + KYC verification",
          body: "Identity and listing verification are both productized, raising trust at both user and inventory layers.",
        },
        {
          title: "AI-first support with escalation",
          body: "Users get immediate help first, with reliable paths to human support when risk or complexity is higher.",
        },
        {
          title: "Bilingual product experience",
          body: "English and Nepali interfaces improve usability and trust across wider user segments in Nepal.",
        },
        {
          title: "Safety reporting surface",
          body: "Clear reporting paths for suspicious listings or behavior improve response quality and platform safety.",
        },
        {
          title: "No-broker transparent discovery",
          body: "A cleaner search experience focused on visibility, clarity, and direct renter-owner value exchange.",
        },
      ];

  const trustControls = isNe
    ? [
        "KYC-सक्षम खाता सुरक्षा र पहिचान प्रवाह",
        "सुरक्षित प्रमाणीकरण, पासवर्ड परिवर्तन र खाता पुनःप्राप्ति मार्ग",
        "लिस्टिङ समीक्षा र सत्यापन संकेतक",
        "सुरक्षा रिपोर्ट र द्रुत सहयोग टिकट उन्नयन",
        "समुदाय निर्देशिका, सुरक्षा केन्द्र र कानुनी नीति पारदर्शिता",
        "प्रशासनिक समीक्षा र भूमिका-आधारित पहुँच नियन्त्रण",
      ]
    : [
        "KYC-enabled identity assurance for account trust",
        "Secure authentication flow, password controls, and account recovery paths",
        "Listing quality review and verification signaling",
        "Safety report channels with support ticket escalation",
        "Transparent policy framework: community, safety, privacy, and legal pages",
        "Administrative moderation with role-based access control",
      ];

  return (
    <div className="about-page">
      <div className="about-bg-orb about-bg-orb-1" />
      <div className="about-bg-orb about-bg-orb-2" />

      <section className="about-hero">
        <div className="container">
          <button type="button" className="about-back-btn" onClick={() => navigate(-1)}>
            {isNe ? "<- फिर्ता" : "<- Back"}
          </button>

          <div className="about-hero-grid">
            <div className="about-hero-copy reveal-up">
              <p className="about-eyebrow">{isNe ? "GharBhada बारे" : "About GharBhada"}</p>
              <h1>
                {isNe
                  ? "विश्वास, सत्यापन र स्पष्टतामा आधारित नेपालको आधुनिक भाडा प्लेटफर्म"
                  : "A modern Nepal rental platform built on trust, verification, and clarity"}
              </h1>
              <p className="about-lead">
                {isNe
                  ? "GharBhada केवल सूचीकरण वेबसाइट होइन। यो सुरक्षित निर्णय, प्रमाणित प्रोफाइल र भाडा प्रक्रियाको प्रत्येक चरणमा विश्वसनीय अनुभव दिन डिजाइन गरिएको डिजिटल पूर्वाधार हो।"
                  : "GharBhada is not just a listing site. It is a trust infrastructure for secure rental decisions, verified interactions, and transparent workflows from search to application."}
              </p>

              <div className="about-hero-actions">
                <button type="button" className="about-btn about-btn-primary" onClick={() => navigate("/find-property")}>
                  {isNe ? "सम्पत्ति खोज्नुहोस्" : "Browse Properties"}
                </button>
                <button type="button" className="about-btn about-btn-secondary" onClick={() => navigate("/list-property")}>
                  {isNe ? "सम्पत्ति सूचीबद्ध गर्नुहोस्" : "List a Property"}
                </button>
              </div>
            </div>

            <div className="about-trust-panel reveal-up reveal-delay-1">
              <h2>{isNe ? "विश्वास सूचक" : "Trust Signals"}</h2>
              <p>
                {isNe
                  ? "सुरक्षा र प्रमाणिकरण-केन्द्रित उत्पादन निर्णयले GharBhada लाई अन्य प्लेटफर्मभन्दा फरक बनाउँछ।"
                  : "Product decisions grounded in security and verification are what make GharBhada different."}
              </p>
              <div className="about-stat-grid">
                {heroStats.map((item) => (
                  <div className="about-stat-card" key={item.label}>
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="container">
          <div className="about-section-head reveal-up">
            <h2>{isNe ? "हाम्रो दृष्टि र मूल सिद्धान्त" : "Our vision and operating pillars"}</h2>
            <p>
              {isNe
                ? "हामीले भाडा बजारको सबैभन्दा ठूला समस्या: विश्वासको अभाव, अस्पष्ट सूचना र जोखिमयुक्त संवादलाई उत्पादन स्तरमै समाधान गर्न प्रयास गरेका छौं।"
                : "We designed GharBhada to solve core rental friction in Nepal: low trust, inconsistent information quality, and risky communication patterns."}
            </p>
          </div>

          <div className="about-pillar-grid">
            {pillars.map((pillar, index) => (
              <article className="about-pillar-card reveal-up" style={{ animationDelay: `${index * 90}ms` }} key={pillar.title}>
                <h3>{pillar.title}</h3>
                <p>{pillar.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-section-alt">
        <div className="container">
          <div className="about-section-head reveal-up">
            <h2>{isNe ? "सत्यापन प्रक्रिया" : "Verification pipeline"}</h2>
            <p>
              {isNe
                ? "विश्वसनीयता एक पटकको कार्य होइन। त्यसैले GharBhada मा सत्यापनलाई बहु-तह प्रक्रिया बनाइएको छ।"
                : "Trust is not a one-time checkbox. GharBhada applies verification as a layered process across the product lifecycle."}
            </p>
          </div>

          <div className="about-flow-grid">
            {verificationFlow.map((item, index) => (
              <article className="about-flow-card reveal-up" style={{ animationDelay: `${index * 85}ms` }} key={item.title}>
                <span className="about-flow-step">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section">
        <div className="container">
          <div className="about-section-head reveal-up">
            <h2>{isNe ? "हामीलाई अलग बनाउने मुख्य विशेषताहरू" : "What makes GharBhada stand out"}</h2>
            <p>
              {isNe
                ? "यो प्लेटफर्म केवल सुन्दर इन्टरफेस होइन। यसको वास्तविक मूल्य निर्धारक भनेका कार्यप्रवाहको गुणस्तर, सुरक्षा-आधारित डिजाइन र प्रयोगकर्ता विश्वास हुन्।"
                : "The difference is not only visual polish. It is workflow quality, safety design, and confidence engineered into everyday rental actions."}
            </p>
          </div>

          <div className="about-feature-grid">
            {standoutFeatures.map((feature, index) => (
              <article className="about-feature-card reveal-up" style={{ animationDelay: `${index * 70}ms` }} key={feature.title}>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-section about-section-alt">
        <div className="container">
          <div className="about-controls-wrap reveal-up">
            <div className="about-controls-copy">
              <h2>{isNe ? "विश्वास नियन्त्रण र सुरक्षा प्रतिबद्धता" : "Trust controls and safety commitments"}</h2>
              <p>
                {isNe
                  ? "हामीले विश्वासलाई प्रचारको दाबी होइन, उत्पादन व्यवहार बनाएका छौं। तलका नियन्त्रणहरू प्रयोगकर्ता सुरक्षा, पारदर्शिता र अनुपालन तयारीको आधार हुन्।"
                  : "At GharBhada, trust is not a marketing line. It is an operational discipline. These controls support user safety, transparency, and compliance readiness."}
              </p>
              <ul>
                {trustControls.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="about-compare-card">
              <h3>{isNe ? "यो किन महत्वपूर्ण छ" : "Why this matters"}</h3>
              <div className="about-compare-row">
                <span>{isNe ? "परम्परागत भाडा अनुभव" : "Traditional rental flow"}</span>
                <p>
                  {isNe
                    ? "अस्पष्ट सूचना, अप्रत्यक्ष संवाद र सत्यापन अभावका कारण निर्णय जोखिम उच्च हुन्छ।"
                    : "Decision quality suffers when data is inconsistent, communication is indirect, and verification is weak."}
                </p>
              </div>
              <div className="about-compare-row">
                <span>{isNe ? "GharBhada अनुभव" : "GharBhada experience"}</span>
                <p>
                  {isNe
                    ? "संरचित जानकारी, प्रत्यक्ष संवाद र बहु-तह विश्वास नियन्त्रणले सुरक्षित र छिटो निर्णयलाई समर्थन गर्छ।"
                    : "Structured listing data, direct messaging, and layered trust controls support safer, faster rental decisions."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta reveal-up">
        <div className="container">
          <h2>
            {isNe
              ? "विश्वसनीय र आधुनिक भाडा अनुभव सुरु गर्न तयार हुनुहुन्छ?"
              : "Ready to experience a more trusted and modern way to rent?"}
          </h2>
          <p>
            {isNe
              ? "सम्पत्ति खोज्न, सूचीबद्ध गर्न वा सुरक्षा सम्बन्धी प्रश्नका लागि तपाईंले चाहिएको सबै मार्गहरू GharBhada मा उपलब्ध छन्।"
              : "Whether you want to rent, list, or verify safety concerns, GharBhada provides practical pathways with trust at the center."}
          </p>

          <div className="about-hero-actions">
            <button type="button" className="about-btn about-btn-primary" onClick={() => navigate("/find-property")}>
              {isNe ? "सम्पत्ति हेर्नुहोस्" : "Find a Home"}
            </button>
            <button type="button" className="about-btn about-btn-secondary" onClick={() => navigate("/safety")}>
              {isNe ? "सुरक्षा केन्द्र" : "Visit Safety Center"}
            </button>
            <button type="button" className="about-btn about-btn-ghost" onClick={() => navigate("/contact")}>
              {isNe ? "सपोर्ट सम्पर्क" : "Contact Support"}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AboutUs;
